import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Building2, Palette, Image, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { API } from './adminConstants';

export function AdminBrandingSection({ 
    organization, 
    orgSettings, 
    setOrgSettings,
    headers,
    refreshOrganization
}) {
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const logoInputRef = useRef(null);

    const handleLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 5MB');
            return;
        }
        
        setUploadingLogo(true);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API}/upload/image`, formData, {
                headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const logoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
            setOrgSettings({ 
                ...orgSettings, 
                branding: { ...orgSettings?.branding, logo_url: logoUrl }
            });
            toast.success('Logo uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleSaveBranding = async () => {
        try {
            await axios.put(`${API}/admin/organization`, orgSettings, { headers });
            toast.success('Organization settings updated');
            refreshOrganization();
        } catch (error) {
            toast.error('Failed to update organization settings');
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-branding">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Organization Branding</h2>
            </div>
            
            {/* Organization Info */}
            <div className="card-base">
                <div className="flex items-center gap-4 mb-6">
                    {organization?.branding?.logo_url ? (
                        <img 
                            src={organization.branding.logo_url} 
                            alt={organization.name}
                            className="w-20 h-20 object-contain rounded-lg bg-gray-50"
                        />
                    ) : (
                        <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Building2 className="w-10 h-10 text-primary" />
                        </div>
                    )}
                    <div>
                        <h3 className="text-xl font-bold">{organization?.name || 'Your Organization'}</h3>
                        <p className="text-muted-foreground">{organization?.tagline || 'Premium Lifestyle Membership'}</p>
                        <p className="text-sm text-primary">{organization?.slug || 'your-slug'}.residentsplus.com</p>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-base">Organization Name</Label>
                        <Input
                            value={orgSettings?.name || organization?.name || ''}
                            onChange={(e) => setOrgSettings({ ...orgSettings, name: e.target.value })}
                            className="mt-2"
                            placeholder="e.g., Oakwood Village"
                            data-testid="org-name-input"
                        />
                    </div>
                    <div>
                        <Label className="text-base">Tagline</Label>
                        <Input
                            value={orgSettings?.tagline || organization?.tagline || ''}
                            onChange={(e) => setOrgSettings({ ...orgSettings, tagline: e.target.value })}
                            className="mt-2"
                            placeholder="e.g., Premium Lifestyle Membership"
                            data-testid="org-tagline-input"
                        />
                    </div>
                </div>
            </div>
            
            {/* Color Theme */}
            <div className="card-base space-y-4">
                <h3 className="font-bold text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5 text-primary" />
                    Color Theme
                </h3>
                <p className="text-sm text-muted-foreground">
                    Customize your organization's color scheme. Colors are in HSL format (e.g., "163 68% 18%").
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-base">Primary Color</Label>
                        <div className="flex items-center gap-2 mt-2">
                            <div 
                                className="w-10 h-10 rounded-lg border"
                                style={{ backgroundColor: `hsl(${orgSettings?.branding?.primary_color || organization?.branding?.primary_color || '163 68% 18%'})` }}
                            />
                            <Input
                                value={orgSettings?.branding?.primary_color || organization?.branding?.primary_color || ''}
                                onChange={(e) => setOrgSettings({ 
                                    ...orgSettings, 
                                    branding: { ...orgSettings?.branding, primary_color: e.target.value }
                                })}
                                placeholder="163 68% 18%"
                                data-testid="primary-color-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-base">Secondary Color</Label>
                        <div className="flex items-center gap-2 mt-2">
                            <div 
                                className="w-10 h-10 rounded-lg border"
                                style={{ backgroundColor: `hsl(${orgSettings?.branding?.secondary_color || organization?.branding?.secondary_color || '160 55% 27%'})` }}
                            />
                            <Input
                                value={orgSettings?.branding?.secondary_color || organization?.branding?.secondary_color || ''}
                                onChange={(e) => setOrgSettings({ 
                                    ...orgSettings, 
                                    branding: { ...orgSettings?.branding, secondary_color: e.target.value }
                                })}
                                placeholder="160 55% 27%"
                                data-testid="secondary-color-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-base">Accent Color</Label>
                        <div className="flex items-center gap-2 mt-2">
                            <div 
                                className="w-10 h-10 rounded-lg border"
                                style={{ backgroundColor: `hsl(${orgSettings?.branding?.accent_color || organization?.branding?.accent_color || '4 68% 68%'})` }}
                            />
                            <Input
                                value={orgSettings?.branding?.accent_color || organization?.branding?.accent_color || ''}
                                onChange={(e) => setOrgSettings({ 
                                    ...orgSettings, 
                                    branding: { ...orgSettings?.branding, accent_color: e.target.value }
                                })}
                                placeholder="4 68% 68%"
                                data-testid="accent-color-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-base">Background Color</Label>
                        <div className="flex items-center gap-2 mt-2">
                            <div 
                                className="w-10 h-10 rounded-lg border"
                                style={{ backgroundColor: `hsl(${orgSettings?.branding?.background_color || organization?.branding?.background_color || '45 23% 95%'})` }}
                            />
                            <Input
                                value={orgSettings?.branding?.background_color || organization?.branding?.background_color || ''}
                                onChange={(e) => setOrgSettings({ 
                                    ...orgSettings, 
                                    branding: { ...orgSettings?.branding, background_color: e.target.value }
                                })}
                                placeholder="45 23% 95%"
                                data-testid="background-color-input"
                            />
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Logo */}
            <div className="card-base space-y-4">
                <h3 className="font-bold text-lg flex items-center gap-2">
                    <Image className="w-5 h-5 text-primary" />
                    Logo
                </h3>
                
                <div className="flex items-center gap-4">
                    {(orgSettings?.branding?.logo_url || organization?.branding?.logo_url) ? (
                        <img 
                            src={orgSettings?.branding?.logo_url || organization?.branding?.logo_url} 
                            alt="Organization logo"
                            className="w-24 h-24 object-contain rounded-lg bg-gray-50 border"
                        />
                    ) : (
                        <div className="w-24 h-24 rounded-lg bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center">
                            <Building2 className="w-10 h-10 text-gray-400" />
                        </div>
                    )}
                    
                    <div className="flex-1">
                        <input
                            type="file"
                            ref={logoInputRef}
                            onChange={handleLogoUpload}
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            data-testid="logo-file-input"
                        />
                        <Button
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                            className="mb-2"
                            variant="outline"
                            data-testid="upload-logo-btn"
                        >
                            {uploadingLogo ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" />
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Upload Logo
                                </>
                            )}
                        </Button>
                        <p className="text-xs text-muted-foreground">
                            Supported formats: JPEG, PNG, WebP, GIF (max 5MB)
                        </p>
                    </div>
                </div>
                
                <div className="pt-4 border-t">
                    <Label className="text-sm text-muted-foreground">Or enter a URL directly</Label>
                    <Input
                        value={orgSettings?.branding?.logo_url || organization?.branding?.logo_url || ''}
                        onChange={(e) => setOrgSettings({ 
                            ...orgSettings, 
                            branding: { ...orgSettings?.branding, logo_url: e.target.value }
                        })}
                        className="mt-2"
                        placeholder="https://example.com/logo.png"
                        data-testid="logo-url-input"
                    />
                </div>
            </div>
            
            {/* Contact Information */}
            <div className="card-base space-y-4">
                <h3 className="font-bold text-lg">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-base">Address</Label>
                        <Textarea
                            value={orgSettings?.contact?.address || organization?.contact?.address || ''}
                            onChange={(e) => setOrgSettings({ 
                                ...orgSettings, 
                                contact: { ...orgSettings?.contact, address: e.target.value }
                            })}
                            className="mt-2"
                            rows={2}
                            data-testid="contact-address-input"
                        />
                    </div>
                    <div>
                        <Label className="text-base">Phone</Label>
                        <Input
                            value={orgSettings?.contact?.phone || organization?.contact?.phone || ''}
                            onChange={(e) => setOrgSettings({ 
                                ...orgSettings, 
                                contact: { ...orgSettings?.contact, phone: e.target.value }
                            })}
                            className="mt-2"
                            data-testid="contact-phone-input"
                        />
                    </div>
                    <div>
                        <Label className="text-base">Email</Label>
                        <Input
                            type="email"
                            value={orgSettings?.contact?.email || organization?.contact?.email || ''}
                            onChange={(e) => setOrgSettings({ 
                                ...orgSettings, 
                                contact: { ...orgSettings?.contact, email: e.target.value }
                            })}
                            className="mt-2"
                            data-testid="contact-email-input"
                        />
                    </div>
                    <div>
                        <Label className="text-base">Website</Label>
                        <Input
                            value={orgSettings?.contact?.website || organization?.contact?.website || ''}
                            onChange={(e) => setOrgSettings({ 
                                ...orgSettings, 
                                contact: { ...orgSettings?.contact, website: e.target.value }
                            })}
                            className="mt-2"
                            data-testid="contact-website-input"
                        />
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <Button 
                onClick={handleSaveBranding}
                className="w-full"
                data-testid="save-branding-btn"
            >
                Save Branding Settings
            </Button>
        </div>
    );
}
